import React from 'react';
import { Alert, Box, Card, CircularProgress, Drawer } from '@mui/material';
import DocumentsSidebar from '../components/DocumentsSidebar';
import DocumentsFileView from '../components/DocumentsFileView';
import DocumentsPreviewDrawer from '../components/DocumentsPreviewDrawer';
import DocumentsActivityDrawer from '../components/DocumentsActivityDrawer';
import DocumentsStats from '../components/DocumentsStats';
import DocumentsToolbar from '../components/DocumentsToolbar';
import DocumentsDialogs from '../components/DocumentsDialogs';
import DocumentsShareDialog from '../components/DocumentsShareDialog';
import { useDocumentsLibrary } from '../hooks/useDocumentsLibrary';

const DocumentsPage: React.FC = () => {
  const lib = useDocumentsLibrary();

  const sidebar = (
    <DocumentsSidebar
      categories={lib.categories}
      selectedId={lib.activeCategoryId}
      totalFiles={lib.totalFiles}
      canManageCategories={lib.canManageCategories}
      onSelect={lib.selectFolder}
    />
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      {(!lib.configured || !lib.driveReady) && (
        <Alert severity="warning">
          {lib.driveMessage
            || 'Google Drive is not configured on the server. Admins: set GOOGLE_DRIVE_SHARED_DRIVE_ID to a Shared Drive id (not a normal folder), set GOOGLE_DRIVE_SERVICE_ACCOUNT_JSON, then restart the backend.'}
        </Alert>
      )}

      <DocumentsStats
        folderCount={lib.categories.length}
        totalFiles={lib.totalFiles}
        storageBytes={lib.storageBytes}
      />

      <DocumentsToolbar
        isMobile={lib.isMobile}
        canManageCategories={lib.canManageCategories}
        selectedFolder={lib.selectedFolder}
        canUpload={lib.canUploadToSelected}
        searchTerm={lib.searchTerm}
        onSearchChange={lib.setSearchTerm}
        typeFilter={lib.typeFilter}
        onTypeFilterChange={lib.setTypeFilter}
        scopeFilter={lib.scopeFilter}
        onScopeFilterChange={lib.setScopeFilter}
        viewMode={lib.viewMode}
        onViewModeChange={lib.setViewMode}
        restoringFolder={lib.restoreCategory.isPending}
        onOpenFolders={() => lib.setMobileSidebarOpen(true)}
        onOpenActivity={() => lib.setActivityOpen(true)}
        onShareFolder={() => lib.setShareFolderOpen(true)}
        onRenameFolder={lib.openRenameFolder}
        onHideFolder={() =>
          lib.selectedFolder
          && lib.setPendingDelete({
            kind: 'folder-hide',
            id: lib.selectedFolder.id,
            name: lib.selectedFolder.name,
          })
        }
        onDeleteFolder={() =>
          lib.selectedFolder
          && lib.setPendingDelete({
            kind: 'folder-delete',
            id: lib.selectedFolder.id,
            name: lib.selectedFolder.name,
          })
        }
        canDeleteFolder={lib.canDeleteSelectedFolder}
        onUnhideFolder={() => lib.selectedFolder && lib.restoreCategory.mutate(lib.selectedFolder.id)}
        onNewFolder={() => lib.openCategoryDialog(lib.selectedFolder?.id ?? null)}
        onUpload={() => lib.openAdd('upload')}
      />

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', minHeight: { md: 480 } }}>
        {!lib.isMobile && (
          <Card sx={{ width: 280, flexShrink: 0, alignSelf: 'stretch' }}>
            {lib.categoriesLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              sidebar
            )}
          </Card>
        )}

        <Box sx={{ flex: 1, minWidth: 0 }}>
          {lib.selectedFolder?.pathOnly ? (
            <Alert severity="info">
              You only have access to a subfolder under “{lib.selectedFolder.name}”. Select the shared
              subfolder to view files. Parent folders are not shared.
            </Alert>
          ) : lib.documentsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : lib.documentsError ? (
            <Alert severity="error">{lib.documentsError}</Alert>
          ) : (
            <DocumentsFileView
              files={lib.filteredFiles}
              categories={lib.categories}
              viewMode={lib.visibleViewMode}
              selectedIds={lib.selectedIds}
              onToggleSelect={lib.toggleSelect}
              onToggleSelectAll={lib.toggleSelectAll}
              sortKey={lib.sortKey}
              sortDir={lib.sortDir}
              onSortChange={lib.handleSortChange}
              canDelete={(doc) => lib.canManageCategories || doc.createdBy === lib.user?.id}
              deleting={lib.deleteDocument.isPending || lib.bulkDelete.isPending}
              canAdd={lib.canUploadToSelected}
              onPreview={lib.openPreview}
              onOpenExternal={lib.openExternal}
              onCopyLink={lib.copyLink}
              onDelete={(doc) => lib.setPendingDelete({ kind: 'document', id: doc.id, name: doc.name })}
              onToggleFavorite={(doc) => lib.toggleFavorite.mutate(doc)}
              onMove={(doc) => lib.startMove([doc.id], doc.categoryId)}
              onUpload={() => lib.openAdd('upload')}
              onCreateDoc={() => lib.openAdd('google_doc')}
              onBulkCopy={() => lib.copyLinks([...lib.selectedIds])}
              onBulkDelete={() =>
                lib.setPendingDelete({ kind: 'bulk', ids: [...lib.selectedIds], count: lib.selectedIds.size })
              }
              onBulkMove={() => lib.startMove([...lib.selectedIds], lib.activeFolders[0]?.id || '')}
            />
          )}
        </Box>
      </Box>

      <Drawer anchor="left" open={lib.mobileSidebarOpen} onClose={() => lib.setMobileSidebarOpen(false)}>
        <Box sx={{ width: 280, pt: 1 }}>{sidebar}</Box>
      </Drawer>

      <DocumentsPreviewDrawer
        doc={lib.previewDoc}
        categories={lib.categories}
        open={Boolean(lib.previewDoc)}
        onClose={() => lib.setPreviewDoc(null)}
        onOpenExternal={lib.openExternal}
      />

      <DocumentsActivityDrawer open={lib.activityOpen} onClose={() => lib.setActivityOpen(false)} />

      <DocumentsShareDialog
        open={lib.shareFolderOpen}
        folder={lib.selectedFolder}
        onClose={() => lib.setShareFolderOpen(false)}
      />

      <DocumentsDialogs
        categories={lib.categories}
        activeFolders={lib.activeFolders}
        selectedFolder={lib.selectedFolder}
        parentCategory={lib.parentCategory}
        formError={lib.formError}
        categoryDialogOpen={lib.categoryDialogOpen}
        categoryName={lib.categoryName}
        onCategoryNameChange={lib.setCategoryName}
        onCloseCategoryDialog={() => lib.setCategoryDialogOpen(false)}
        onCreateCategory={() => lib.createCategory.mutate()}
        creatingCategory={lib.createCategory.isPending}
        renameFolderOpen={lib.renameFolderOpen}
        renameFolderName={lib.renameFolderName}
        onRenameFolderNameChange={lib.setRenameFolderName}
        onCloseRenameFolder={() => lib.setRenameFolderOpen(false)}
        onRenameFolder={() => lib.renameCategory.mutate()}
        renamingFolder={lib.renameCategory.isPending}
        moveDocIds={lib.moveDocIds}
        moveTargetId={lib.moveTargetId}
        onMoveTargetChange={lib.setMoveTargetId}
        onCloseMove={lib.closeMoveDialog}
        onConfirmMove={() => lib.moveDocuments.mutate({ ids: lib.moveDocIds, categoryId: lib.moveTargetId })}
        moving={lib.moveDocuments.isPending}
        addOpen={lib.addOpen}
        addKind={lib.addKind}
        addName={lib.addName}
        addUrl={lib.addUrl}
        addFile={lib.addFile}
        onAddKindChange={lib.setAddKind}
        onAddNameChange={lib.setAddName}
        onAddUrlChange={lib.setAddUrl}
        onAddFileChange={lib.setAddFile}
        onCloseAdd={() => lib.setAddOpen(false)}
        onSubmitAdd={() => lib.addDocument.mutate()}
        adding={lib.addDocument.isPending}
        pendingDelete={lib.pendingDelete}
        onClosePendingDelete={() => lib.setPendingDelete(null)}
        onConfirmHideFolder={(id) => lib.deactivateCategory.mutate(id)}
        onConfirmDeleteFolder={(id) => lib.deleteEmptyCategory.mutate(id)}
        onConfirmDeleteDocument={(id) => lib.deleteDocument.mutate(id)}
        onConfirmBulkDelete={(ids) => lib.bulkDelete.mutate(ids)}
      />
    </Box>
  );
};

export default DocumentsPage;
